import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';

const GRAFFITI_FAQ = [
  {
    question: 'How quickly can you remove graffiti from my property?',
    answer: 'We offer prompt scheduling and can often provide same-day or next-day graffiti removal for commercial properties. Fast removal helps prevent repeat vandalism.'
  },
  {
    question: 'Is your graffiti removal process safe for all surfaces?',
    answer: {
      intro: 'Yes, we use surface-appropriate methods for graffiti removal, including:',
      list: [
        'Brick and masonry',
        'Concrete',
        'Metal',
        'Painted surfaces',
        'Glass'
      ],
      outro: 'Our technicians assess each surface and use the safest, most effective removal technique.'
    }
  },
  {
    question: 'Can you remove all types of graffiti, including spray paint and markers?',
    answer: 'Yes! We have professional-grade removers and equipment to tackle spray paint, markers, stickers, and more. Most graffiti can be fully removed without damaging the underlying surface.'
  }
];

const GraffitiRemoval = () => {
  const serviceSEO = {
    title: 'Graffiti Removal Services | Copper Canyon Pressure Wash',
    description: 'Professional graffiti removal in Phoenix, AZ. Fast, safe removal from all surfaces. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Graffiti Removal',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Graffiti Removal'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Graffiti Removal Services | Copper Canyon Pressure Wash"
        description="Professional graffiti removal in Phoenix, AZ. Remove spray paint & tags. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="vandalism removal, spray paint removal, Phoenix graffiti cleaning, tag removal, building restoration, wall cleaning, surface restoration, property maintenance, emergency graffiti removal, commercial property cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Graffiti Removal Banner">
        <picture>
          <source srcSet="/grafitti_hero.avif" type="image/avif" />
          <source srcSet="/grafitti_hero.webp" type="image/webp" />
        <img 
          src="/grafitti_hero.jpeg" 
          alt="Professional graffiti removal in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Graffiti Removal</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Graffiti Removal Services</h2>
        <p className="mb-6 text-gray-700">Our team provides fast, effective graffiti removal for commercial properties, HOAs, and public spaces. We use safe, proven methods to restore your surfaces and deter future vandalism.</p>
        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Why Choose Us?</h3>
        <ul className="list-disc pl-6 mb-6 text-gray-700">
          <li>Rapid response and flexible scheduling</li>
          <li>Safe, surface-appropriate removal methods</li>
          <li>Experienced, professional technicians</li>
          <li>Eco-friendly cleaning solutions</li>
          <li>100% satisfaction guarantee</li>
        </ul>
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Graffiti Removal!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our graffiti removal services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={GRAFFITI_FAQ} title="Frequently Asked Questions About Graffiti Removal" />
    </div>
  );
};

export default GraffitiRemoval; 